import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import { of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import {
	OpeningHoursModel,
	Resource,
	ResourceApiService,
} from "@3volutionsag/ngx-rooms-lib";

import { RequestAuthPolicyService } from "../../authentication/request-auth-policy.service";
import { ConfigurationState } from "../../configuration/state/configuration.state";
import { HttpRequestHelperService } from "../../shared/http-request-helper.service";

import {
	FetchResources,
	FetchResourcesFailure,
	FetchResourcesSuccess,
	LoadOpeningHoursForDay,
} from "./resource.actions";

import type { NgxsAfterBootstrap } from "@ngxs/store";

export interface ResourceStateModel {
	openingHours: Record<number, OpeningHoursModel[]>;
	locationId?: number;
	resources: Resource[];
	loading: boolean;
	loaded: boolean;
	error: any | null;
}

@State<ResourceStateModel>({
	name: "resource",
	defaults: {
		openingHours: {},
		resources: [],
		loading: false,
		loaded: false,
		error: null,
	},
})
@Injectable()
export class ResourceState implements NgxsAfterBootstrap {
	private resourceApiService = inject(ResourceApiService);
	private httpRequestHelper = inject(HttpRequestHelperService);
	private requestAuthPolicy = inject(RequestAuthPolicyService);

	constructor(private store: Store) {}

	@Selector()
	public static openingHours(state: ResourceStateModel) {
		return state.openingHours;
	}

	@Selector()
	public static resources(state: ResourceStateModel): Resource[] {
		return state.resources;
	}

	@Selector()
	public static isLoading(state: ResourceStateModel): boolean {
		return state.loading;
	}

	@Selector()
	public static hasError(state: ResourceStateModel): boolean {
		return !!state.error;
	}

	@Selector([ResourceState.openingHours])
	public static openingHoursOfDay(openingHours: ResourceStateModel["openingHours"]) {
		return (day: moment.Moment) => {
			const dayIndex = this.getDayIndex(day);
			if (Object.prototype.hasOwnProperty.call(openingHours, dayIndex)) {
				return openingHours[dayIndex];
			}

			return [];
		};
	}

	ngxsAfterBootstrap(ctx: StateContext<ResourceStateModel>): void {
		// Reset state on first load as a sort of cache buster
		ctx.setState({
			locationId: undefined,
			openingHours: {},
			resources: [],
			loading: false,
			loaded: false,
			error: null,
		});
	}

	@Action(FetchResources)
	public fetchResources(ctx: StateContext<ResourceStateModel>) {
		const state = ctx.getState();

		// If resources already loaded, don't fetch again
		if (state.loaded && state.resources.length > 0) {
			return;
		}

		ctx.patchState({
			loading: true,
			error: null,
		});

		return this.resourceApiService.findDsResources().pipe(
			tap((resources) => ctx.dispatch(new FetchResourcesSuccess(resources))),
			catchError((error) => {
				return ctx.dispatch(new FetchResourcesFailure(error));
			}),
		);
	}

	@Action(FetchResourcesSuccess)
	public fetchResourcesSuccess(
		ctx: StateContext<ResourceStateModel>,
		action: FetchResourcesSuccess,
	) {
		ctx.patchState({
			resources: action.resources,
			loading: false,
			loaded: true,
			error: null,
		});
	}

	@Action(FetchResourcesFailure)
	public fetchResourcesFailure(
		ctx: StateContext<ResourceStateModel>,
		action: FetchResourcesFailure,
	) {
		ctx.patchState({
			loading: false,
			error: action.error,
		});
	}

	@Action(LoadOpeningHoursForDay)
	public loadOpeningHoursForDay(
		ctx: StateContext<ResourceStateModel>,
		action: LoadOpeningHoursForDay,
	) {
		const dayIndex = ResourceState.getDayIndex(action.day);
		const state = ctx.getState();
		if (Object.prototype.hasOwnProperty.call(state.openingHours, dayIndex)) {
			return;
		}

		const dsConfig = this.store.selectSnapshot(ConfigurationState.dsConfiguration);
		if (!dsConfig?.resourceId) {
			return;
		}

		this.httpRequestHelper.prepareApiRequest(
			this.resourceApiService,
			this.requestAuthPolicy.forResourceRead(),
		);

		const locationId = state.locationId
			? of(state.locationId)
			: this.resourceApiService.getById(dsConfig.resourceId).pipe(
					map((r) => r.locationId),
					tap((lid) => {
						ctx.patchState({
							locationId: lid,
						});
					}),
				);

		return locationId.pipe(
			switchMap((lid) => this.resourceApiService.findAllOpeningHours(action.day, lid)),
			tap((openingHours) => {
				ctx.setState(
					patch<ResourceStateModel>({
						openingHours: patch<ResourceStateModel["openingHours"]>({
							[dayIndex]: openingHours,
						}),
					}),
				);
			}),
		);
	}

	private static getDayIndex(day: moment.Moment): number {
		return day.clone().startOf("D").unix();
	}
}
